import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { RadioGroup, Radio } from "@/components/ui/Radio";
import { Stack } from "@/components/ui/Stack";
import { FormControl } from "@/components/ui/FormControl";
import { Input } from "@/components/ui/Input";
import classes from "./BankDetails.module.scss";
import { useDonationFormStore } from "@/stores";
import type { BankDetailsProps } from "./BankDetailsProps";
import { clsx } from "clsx";
import { useTranslation } from "@/hooks/useTranslation";
import { useService } from "@/hooks/useService";
import { trackError } from "@/utils/error-monitoring";
import { Spinner } from "../ui/Spinner";
import type { Option } from "@/types/Form";
import { formatDate } from "@/utils/formatters";
import { BankDetailsSortCode } from "./BankDetailsSortCode";
import { BankDetailsNumberField } from "./BankDetailsNumberField";
import { useBankDetailsErrorsStore } from "./useBankDetailsErrorsStore";
import { useBankDetailsValidation } from "./useBankDetailsValidation";
import { BankDetailsDirectDebitGuaranteeModal } from "./BankDetailsDirectDebitGuaranteeModal";

const acountNumberErrors = [
  "Please enter a valid account number",
  "The account is not Direct Debit Capable",
];

function fomatDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

const today = fomatDateParam(new Date());

export function BankDetails(props: BankDetailsProps) {
  const { config, className, ref } = props;

  const { bankDetails, setBankDetails } = useDonationFormStore();
  const [loading, setLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const disclaimerRef = useRef<HTMLDivElement>(null);
  const [showGuaranteeModal, setShowGuaranteeModal] = useState(false);
  const [touches, setTouches] = useState({
    account_holder: false,
    account_number: false,
    sort_code: false,
  });

  const t = useTranslation("bank_details");
  const { getRetrieveAvailableDates, validateBankDetails } = useService();
  const accountNumberRef = useRef<HTMLInputElement>(null);
  const accountHolderRef = useRef<HTMLInputElement>(null);
  const sortCodeRef = useRef<{ focus: () => void } | null>(null);

  const { account_holder, account_number, sort_code, start_date } = bankDetails;

  const errors = useBankDetailsErrorsStore((state) => state.errors);
  const isValidated = useBankDetailsErrorsStore((state) => state.isValidated);
  const { validate, validateField } = useBankDetailsValidation();
  const setErrors = useBankDetailsErrorsStore((state) => state.setErrors);

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const errors = validate(bankDetails);

      if (errors) {
        if (errors.account_holder) {
          accountHolderRef.current?.focus();
        } else if (errors.account_number) {
          accountNumberRef.current?.focus();
        } else if (errors.sort_code) {
          sortCodeRef.current?.focus();
        }

        return {
          isValid: false,
        };
      }

      const validationResponse = await validateBankDetails({
        account_number: bankDetails.account_number,
        sort_code: bankDetails.sort_code,
      });

      if (validationResponse === true) {
        return {
          isValid: true,
        };
      }

      if (typeof validationResponse === "boolean") {
        accountNumberRef.current?.focus();
        setErrors({
          account_number: t("errors.account-number-invalid") as string,
        });
        return {
          isValid: false,
        };
      }

      if (acountNumberErrors.includes(validationResponse)) {
        accountNumberRef.current?.focus();
        setErrors({
          account_number: validationResponse,
        });
      } else if (validationResponse === "Please enter a valid sort code") {
        sortCodeRef.current?.focus();
        setErrors({
          sort_code: validationResponse,
        });
      } else {
        accountNumberRef.current?.focus();
        setErrors({
          account_number: t("errors.account-number-invalid") as string,
          sort_code: t("errors.sort-code-invalid") as string,
        });
      }

      return {
        isValid: false,
      };
    },
  }));

  useEffect(() => {
    if (disclaimerRef.current) {
      const link = disclaimerRef.current.querySelector("a");
      if (link) {
        const handleLinkClick = (event: MouseEvent) => {
          event.preventDefault();
          setShowGuaranteeModal(true);
        };

        link.addEventListener("click", handleLinkClick);

        return () => {
          link.removeEventListener("click", handleLinkClick);
        };
      }
    }
  }, [disclaimerRef]);

  useEffect(() => {
    const fetchAvailableDates = async () => {
      setLoading(true);
      try {
        const response = await getRetrieveAvailableDates(today);
        const dates = response["available-dates"];

        if (!Array.isArray(dates) || dates.length === 0) {
          throw new Error("No available dates found");
        }

        setAvailableDates(dates);
        setBankDetails({
          ...bankDetails,
          start_date: dates[0],
        });
      } catch (error) {
        trackError(error as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchAvailableDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRetrieveAvailableDates, setBankDetails]);

  const handleChangeAccountHolder = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;

    validateField("account_holder", value);

    setBankDetails({
      ...bankDetails,
      account_holder: value,
    });
  };

  const handleChangeAccountNumber = (value: string) => {
    validateField("account_number", value);

    setBankDetails({
      ...bankDetails,
      account_number: value,
    });
  };

  const handleChangeSortCode = (value: string) => {
    validateField("sort_code", value);

    setBankDetails({
      ...bankDetails,
      sort_code: value,
    });
  };

  const handleChangeStartDate = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    if (isValidated) validateField("start_date", value);
    setBankDetails({
      ...bankDetails,
      start_date: value,
    });
  };

  const options: Option[] = availableDates.map((date) => {
    const formattedDate = formatDate(date);

    return {
      value: date,
      label: formattedDate,
    };
  });

  return (
    <>
      <BankDetailsDirectDebitGuaranteeModal
        open={showGuaranteeModal}
        onClose={() => setShowGuaranteeModal(false)}
      >
        <div className={classes.formatter}>
          {t(config["bank-details-guarantee"].i18n)}
        </div>
      </BankDetailsDirectDebitGuaranteeModal>

      <div className={clsx(classes.wrapper, className)}>
        <Stack gap="md">
          <FormControl
            label={t(config["bank-details-account-holder-label"].i18n)}
            error={isValidated ? errors.account_holder : null}
            isValid={
              (isValidated || touches.account_holder) && !errors.account_holder
            }
          >
            <Input
              ref={accountHolderRef}
              name="account_holder"
              value={account_holder}
              onChange={handleChangeAccountHolder}
              onBlur={() => setTouches({ ...touches, account_holder: true })}
            />
          </FormControl>

          <FormControl
            label={t(config["bank-details-account-number-label"].i18n)}
            error={isValidated ? errors.account_number : null}
            isValid={
              (isValidated || touches.account_number) && !errors.account_number
            }
          >
            <BankDetailsNumberField
              ref={accountNumberRef}
              name="account_number"
              value={account_number}
              onChange={handleChangeAccountNumber}
              maxLength={8}
              onBlur={() => setTouches({ ...touches, account_number: true })}
            />
          </FormControl>

          <BankDetailsSortCode
            label={t(config["bank-details-sort-code-label"].i18n) as string}
            initialValue={sort_code}
            onChange={handleChangeSortCode}
            error={isValidated ? errors.sort_code : null}
            isValid={(isValidated || touches.sort_code) && !errors.sort_code}
            ref={sortCodeRef}
            onBlur={() => setTouches({ ...touches, sort_code: true })}
          />
          {loading && (
            <div
              style={{
                height: 112,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Spinner />
            </div>
          )}
          {!loading && options.length > 0 && (
            <FormControl
              type="fieldset"
              label={t(config["bank-details-start-date-label"].i18n)}
            >
              <RadioGroup
                direction="column"
                name="start_date"
                value={start_date}
                onChange={handleChangeStartDate}
              >
                {options.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </RadioGroup>
            </FormControl>
          )}

          <div className={classes.formatter}>
            {t(config["bank-details-acknowledgement"].i18n, {
              direct_debit_link: `<a href="${config["bank-details-direct-debit-file"]?.src ?? ""}" target="_blank" rel="noopener">Direct Debit Instruction</a>`,
            })}
          </div>

          <div className={classes.formatter} ref={disclaimerRef}>
            {t(config["bank-details-disclaimer"].i18n, {
              linkToDirectDebitGuarantee: "#",
            })}
          </div>
        </Stack>
      </div>
    </>
  );
}
